import { generatePath, Link, useParams } from "react-router-dom";

import {
  Button,
  ConsentSignedIcon,
  ConsultationIcon,
  OrderNoteIcon,
} from "~/common/components";
import { ChangeIcon } from "~/common/components/icons/ChangeIcon";
import {
  MedicationInteractionsIcon,
  PrescriptionRequestIcon,
} from "~/common/components/icons/Orders";
import { PatientFeedIcon } from "~/common/components/icons/PatientFeedIcon";
import { BUTTON_VARIANTS } from "~/common/constants";
import { formatMedicationName } from "~/common/utils";
import {
  PatientMedicationDetailsDrawerProps,
  SelectedPatientCheckin,
} from "~/providers/components";
import { NoteFeedExportDownloadLink } from "~/providers/components/pdf/NoteFeedExport/NoteFeedExportDownloadLink";
import { ACTIVITY_FEED_CODES, RELATED_MODEL } from "~/providers/constants";
import { ROUTES } from "~/providers/router";
import {
  getCheckinSubmittedAction,
  getCheckinSubmittedTitle,
  getFeedDescription,
  getIconByCode,
  getPatientPromotedToIntakeTitle,
} from "~/providers/utils";
import { getExtraInfo } from "~/providers/utils/activityFeed/activityFeedCardExtraInfoByCode";
import { PatientFeed } from "~/providers/utils/types/interactions";
import {
  BundleFeed,
  CheckInOrderedFeed,
  CheckInSubmittedFeed,
  ConsentAcknowledgedFeed,
  ConsultationStatusContext,
  EngagementStatusContext,
  FeedData,
  MedicationFeed,
  MedicationFeedContext,
  NoteFeed,
  PatientPromotedToIntakeFeed,
  PrescriptionFeed,
  PrescriptionFeedContext,
  RecurrentCheckInOrderedFeed,
  SELECTED_PATIENT_CHECKIN_TYPE,
} from "~/providers/utils/types/patientFeeds";

export const getMedicationFeedInternalNote = (feed: MedicationFeed) =>
  feed.context.data?.internal_note;

interface UseGetActivityFeedCardDataProps {
  viewSignedConsents: (signedConsentsActivity: ConsentAcknowledgedFeed) => void;
  viewPatientCheckInDetails: (
    selectedPatientCheckinInfo: SelectedPatientCheckin,
  ) => void;
  viewMedicationDetails: (
    medication: PatientMedicationDetailsDrawerProps,
  ) => void;
}

const actionLinkClassName =
  "w-fit p-0 !text-primary-600 no-underline font-medium";

export const useGetActivityFeedCardData = ({
  viewSignedConsents,
  viewPatientCheckInDetails,
  viewMedicationDetails,
}: UseGetActivityFeedCardDataProps) => {
  const { patientId } = useParams();

  const getPrescriptionAction = (relatedModelId: string) => (
    <Link
      to={`${ROUTES.prescriptionRequest}?id=${relatedModelId}`}
      className={actionLinkClassName}
    >
      Go to Request
    </Link>
  );

  const getMedicationAction = (context: MedicationFeedContext) => (
    <Button
      variant={BUTTON_VARIANTS.outline_none}
      className={actionLinkClassName}
      onClick={() =>
        viewMedicationDetails({
          medication: {
            medication_detail: context.data,
            created_at: context.data.created_at,
          },
          medicationName: formatMedicationName(context.medication),
        })
      }
    >
      See details
    </Button>
  );

  const getProviderPrescriptionAction = (context: PrescriptionFeedContext) => (
    <Button
      variant={BUTTON_VARIANTS.outline_none}
      className={actionLinkClassName}
      onClick={() =>
        viewMedicationDetails({
          medication: {
            medication_detail: context.data.patient_medications_details[0],
            created_at: context.data.patient_medications_details[0].created_at,
          },
          medicationName: formatMedicationName(context.data.medication),
        })
      }
    >
      See details
    </Button>
  );

  const getOldFeedData = (feed: PatientFeed): FeedData | undefined => {
    const data = {
      [RELATED_MODEL.patient_checkin_schedule]: {
        title: feed.description,
        icon: getIconByCode(
          (feed as CheckInSubmittedFeed).context.checkin_code,
        ),
      },
      [RELATED_MODEL.patient_bundle]: {
        title: feed.description,
        icon:
          "bundle" in feed.context
            ? getIconByCode(
                feed.context.bundle.patient_checkins[0].checkin.code,
              )
            : undefined,
      },
      [RELATED_MODEL.patient_medication]: {
        title: feed.description,
        icon: MedicationInteractionsIcon,
      },
      [RELATED_MODEL.script_request]: {
        title: feed.description,
        icon: PrescriptionRequestIcon,
        actions: getPrescriptionAction(feed.related_model_id),
      },
      [RELATED_MODEL.note]: {
        title: feed.description,
        icon: () => OrderNoteIcon({}),
      },
      [RELATED_MODEL.patient]: {
        title: feed.description,
        icon: ConsultationIcon,
      },
      [RELATED_MODEL.patient_checkin]: {
        title: feed.description,
        icon: PrescriptionRequestIcon,
      },
      [RELATED_MODEL.consultation]: {
        title: feed.description,
        icon: ConsultationIcon,
      },
      [RELATED_MODEL.consultation_details]: {
        title: feed.description,
        icon: ConsultationIcon,
      },
      [RELATED_MODEL.organization_patient]: undefined,
    };
    return data[feed.related_model_type];
  };

  const getPrescriptionInternalNote = (feed: PrescriptionFeed) =>
    feed.context.internal_note;

  const getConsultationAction = (id: string) => (
    <Link
      to={generatePath(ROUTES.consultationPreview, { patientId: id })}
      className={actionLinkClassName}
    >
      Go to consultation
    </Link>
  );

  const getFeedData = (feed: PatientFeed): FeedData | undefined => {
    if (!feed.code) return getOldFeedData(feed);
    if (!patientId) return;

    const activityFeedCardDataByCode = {
      [ACTIVITY_FEED_CODES.consultation_requested]: {
        title: "Consultation requested",
        icon: ConsultationIcon,
        actions: getConsultationAction(patientId),
      },
      [ACTIVITY_FEED_CODES.consultation_terminated]: {
        title: "Consultation closed",
        icon: ConsultationIcon,
        actions: getConsultationAction(patientId),
        internalNote: (feed.context as ConsultationStatusContext).internal_note,
      },
      [ACTIVITY_FEED_CODES.consultation_re_opened]: {
        title: "Consultation re-opened",
        icon: ConsultationIcon,
        actions: getConsultationAction(patientId),
        internalNote: (feed.context as ConsultationStatusContext).internal_note,
      },
      [ACTIVITY_FEED_CODES.closed_after_screening]: {
        title: "Consultation closed",
        icon: ConsultationIcon,
        actions: getConsultationAction(patientId),
        internalNote: (feed.context as ConsultationStatusContext).internal_note,
      },
      [ACTIVITY_FEED_CODES.patient_admitted_into_practice]: {
        title: "Admitted to practice",
        icon: PatientFeedIcon,
        internalNote: (feed.context as EngagementStatusContext).internal_note,
      },
      [ACTIVITY_FEED_CODES.patient_promoted_to_intake]: {
        get title() {
          return getPatientPromotedToIntakeTitle(
            feed as PatientPromotedToIntakeFeed,
          );
        },
        actions: getConsultationAction(patientId),
        icon: ConsultationIcon,
      },
      [ACTIVITY_FEED_CODES.patient_archived]: {
        title: "Archived",
        icon: PatientFeedIcon,
        internalNote: (feed.context as EngagementStatusContext).internal_note,
      },
      [ACTIVITY_FEED_CODES.consultation_completed]: {
        title: "Consultation completed",
        icon: ConsultationIcon,
        actions: getConsultationAction(patientId),
      },
      [ACTIVITY_FEED_CODES.consultation_report_completed]: {
        title: "Consultation report (H&P) completed",
        icon: ConsultationIcon,
        actions: getConsultationAction(patientId),
      },
      [ACTIVITY_FEED_CODES.checkin_ordered]: {
        title: "Ordered",
        icon: getIconByCode((feed as CheckInOrderedFeed).context.checkin_code),
        internalNote: (feed as CheckInOrderedFeed).context.data?.internal_note,
        outboundNote: (feed as CheckInOrderedFeed).context.data?.note,
        actions: (
          <Button
            variant={BUTTON_VARIANTS.outline_none}
            className={actionLinkClassName}
            onClick={() =>
              viewPatientCheckInDetails({
                feed: feed as CheckInOrderedFeed,
                isProviderOrdered: true,
                type: SELECTED_PATIENT_CHECKIN_TYPE.regular,
              })
            }
          >
            See details
          </Button>
        ),
      },
      [ACTIVITY_FEED_CODES.checkin_missed]: {
        title: "Missed",
        icon: getIconByCode((feed as CheckInOrderedFeed).context.checkin_code),
      },
      [ACTIVITY_FEED_CODES.medication_added]: {
        title: "Medication added",
        icon: MedicationInteractionsIcon,
        internalNote: getMedicationFeedInternalNote(feed as MedicationFeed),
        actions: getMedicationAction((feed as MedicationFeed).context),
      },
      [ACTIVITY_FEED_CODES.medication_modified]: {
        title: "Medication modified",
        icon: MedicationInteractionsIcon,
        internalNote: getMedicationFeedInternalNote(feed as MedicationFeed),
        actions: getMedicationAction((feed as MedicationFeed).context),
      },
      [ACTIVITY_FEED_CODES.prescription_paused]: {
        title: "Prescriptions paused",
        icon: PrescriptionRequestIcon,
        internalNote: getPrescriptionInternalNote(feed as PrescriptionFeed),
        actions: getProviderPrescriptionAction(
          feed.context as PrescriptionFeedContext,
        ),
      },
      [ACTIVITY_FEED_CODES.prescription_resumed]: {
        title: "Prescriptions resumed",
        icon: PrescriptionRequestIcon,
        internalNote: getPrescriptionInternalNote(feed as PrescriptionFeed),
        actions: getProviderPrescriptionAction(
          feed.context as PrescriptionFeedContext,
        ),
      },
      [ACTIVITY_FEED_CODES.medication_discontinued]: {
        title: "Medication discontinued",
        icon: MedicationInteractionsIcon,
        internalNote: getMedicationFeedInternalNote(feed as MedicationFeed),
        actions: getMedicationAction((feed as MedicationFeed).context),
      },
      [ACTIVITY_FEED_CODES.note_added]: {
        title: "Note added",
        internalNote: (feed as NoteFeed).context.note,
        icon: () => OrderNoteIcon({}),
        actions: <NoteFeedExportDownloadLink noteFeed={feed as NoteFeed} />,
      },
      [ACTIVITY_FEED_CODES.prescription_requested]: {
        title: "Prescription requested",
        icon: PrescriptionRequestIcon,
        actions: getPrescriptionAction(feed.related_model_id),
      },
      [ACTIVITY_FEED_CODES.prescription_cancelled]: {
        title: "Prescription request cancelled",
        icon: PrescriptionRequestIcon,
        actions: getPrescriptionAction(feed.related_model_id),
      },
      [ACTIVITY_FEED_CODES.prescription_sent_to_pharmacy]: {
        title: "Prescription sent to Pharmacy",
        icon: PrescriptionRequestIcon,
        actions: getPrescriptionAction(feed.related_model_id),
      },
      [ACTIVITY_FEED_CODES.prescription_issue_reported]: {
        title: "Prescription issue reported",
        icon: PrescriptionRequestIcon,
        actions: getPrescriptionAction(feed.related_model_id),
      },
      [ACTIVITY_FEED_CODES.checkin_submitted]: {
        get title() {
          return getCheckinSubmittedTitle(feed as CheckInSubmittedFeed);
        },
        icon: getIconByCode(
          (feed as CheckInSubmittedFeed).context.checkin_code,
          (feed as CheckInSubmittedFeed).context.creation_type,
        ),
        actions: getCheckinSubmittedAction(
          feed as CheckInSubmittedFeed,
          viewPatientCheckInDetails,
        ),
      },
      [ACTIVITY_FEED_CODES.bundle_ordered]: {
        title: "Ordered",
        icon: () =>
          getIconByCode(
            (feed as BundleFeed).context.bundle.patient_checkins[0].checkin
              .code,
          )(),
        actions: (
          <Button
            variant={BUTTON_VARIANTS.outline_none}
            className={actionLinkClassName}
            onClick={() =>
              viewPatientCheckInDetails({
                feed: feed as BundleFeed,
                isProviderOrdered: true,
                type: SELECTED_PATIENT_CHECKIN_TYPE.bundle,
              })
            }
          >
            See details
          </Button>
        ),
      },
      [ACTIVITY_FEED_CODES.recurrent_checkin_ordered]: {
        title: "Ordered",
        icon: getIconByCode(
          (feed as RecurrentCheckInOrderedFeed).context.checkin_code,
        ),
        internalNote: (feed as CheckInOrderedFeed).context.data?.internal_note,
        actions: (
          <Button
            variant={BUTTON_VARIANTS.outline_none}
            className={actionLinkClassName}
            onClick={() =>
              viewPatientCheckInDetails({
                feed: feed as RecurrentCheckInOrderedFeed,
                isProviderOrdered: true,
                type: SELECTED_PATIENT_CHECKIN_TYPE.scheduled,
              })
            }
          >
            See details
          </Button>
        ),
      },
      [ACTIVITY_FEED_CODES.primary_provider_changed]: {
        title: "Primary provider changed",
        icon: ChangeIcon,
      },
      [ACTIVITY_FEED_CODES.patient_signed_consent_forms]: {
        get title() {
          const organizationName = (feed as unknown as ConsentAcknowledgedFeed)
            .organization_patient?.organization?.name;
          return organizationName
            ? `Agreed to the consent forms for ${organizationName}`
            : "Agreed to the consent forms";
        },
        icon: ConsentSignedIcon,
        actions: (
          <Button
            variant={BUTTON_VARIANTS.outline_none}
            className={actionLinkClassName}
            onClick={() =>
              viewSignedConsents(feed as unknown as ConsentAcknowledgedFeed)
            }
          >
            See details
          </Button>
        ),
      },
      [ACTIVITY_FEED_CODES.ongoing_care]: undefined,
      [ACTIVITY_FEED_CODES.screening_started]: undefined,
      [ACTIVITY_FEED_CODES.screening_submitted]: undefined,
      [ACTIVITY_FEED_CODES.screening_completed]: undefined,
      [ACTIVITY_FEED_CODES.intake_started]: undefined,
      [ACTIVITY_FEED_CODES.intake_submitted]: undefined,
      [ACTIVITY_FEED_CODES.checkin_deleted]: undefined,
      [ACTIVITY_FEED_CODES.patient_created]: undefined,
      [ACTIVITY_FEED_CODES.appointment_scheduled]: undefined,
    } as const;

    const cardData = activityFeedCardDataByCode[feed.code];

    if (!cardData) return undefined;

    return {
      ...cardData,
      description: getFeedDescription(feed),
      extraInfo: getExtraInfo(feed),
    };
  };

  return { getFeedData };
};
